//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { deserializeCreatorsCampaignsProposalDetailsData } from '@apis/creators-campaigns/creators-proposals/CreatorsProposalApiUtils';
import {
  BulkProposalUpdateResponse,
  CreatorsCampaignsBulkOrderEligibleProductsRequest,
  CreatorsCampaignsBulkOrderEligibleProductsResponse,
  CreatorsCampaignsBulkProposalStatusRequest,
  CreatorsCampaignsProposalDetailsRequest,
  CreatorsCampaignsProposalDetailsResponse,
  CreatorsCampaignsProposalOrderEligibleProductsResponse,
  CreatorsCampaignsProposalStatusRequest,
} from '@apis/creators-campaigns/creators-proposals/CreatorsProposalsApiModel';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';
const CREATOR_CAMPAIGNS_DETAILS_DEFAULT = '/api/v1/campaigns';

const creatorsCampaignsFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'affable_gateway',
    `${CREATOR_CAMPAIGNS_DETAILS_DEFAULT}${endpoint}`,
    init,
  );
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/proposals/:creatorId
 *
 * Get Proposal Details by campaignSlugName and creatorId
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creatorId - The unique identifier for the proposal
 */

export const getCreatorsCampaignsProposalDetails = async (
  req: CreatorsCampaignsProposalDetailsRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsProposalDetailsResponse> => {
  const { campaignSlugName, creatorId } = req;

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/proposals/${creatorId}?`,
    {
      ...init,
      method: 'GET',
    },
  );

  const campaignProposalDetails = await res.json<{
    data: CreatorsCampaignsProposalDetailsResponse;
  }>((res) => ({
    data: deserializeCreatorsCampaignsProposalDetailsData(res.data),
  }));

  return campaignProposalDetails.data;
};

/**
 * PUT /api/v1/campaigns/:campaignSlugName/proposals/:creatorId
 *
 * Update Proposal Status by campaignSlugName, creatorId and proposalStatus with Affiliates
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creatorId - The unique identifier for the Creator
 * @param req.proposalStatus - Proposal Status value i.e ['approved', 'rejected]
 */
export const updateCreatorsCampaignsProposalStatusWithAffiliates = async (
  req: CreatorsCampaignsProposalStatusRequest,
  init?: RequestInit,
): Promise<void> => {
  const { campaignSlugName, creatorId, requestBody } = req;

  await creatorsCampaignsFetch(`/${campaignSlugName}/proposals/${creatorId}?`, {
    ...init,
    method: 'PUT',
    body: JSON.stringify(requestBody),
  });
};

/**
 * GET /api/v1/campaigns/:campaignSlugName/proposals/:creatorId/order-eligible-products
 *
 * Get Order Eligible Products in a Proposal by campaignSlugName and creatorId
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creatorId - The unique identifier for the proposal
 */
export const getCreatorsCampaignsProposalOrderEligibleProducts = async (
  req: CreatorsCampaignsProposalDetailsRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsProposalOrderEligibleProductsResponse> => {
  const { campaignSlugName, creatorId } = req;

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/proposals/${creatorId}/order-eligible-products`,
    {
      ...init,
      method: 'GET',
    },
  );
  const orderEligibleOrders = await res.json<{
    data: CreatorsCampaignsProposalOrderEligibleProductsResponse;
  }>();

  return orderEligibleOrders.data;
};

/**
 * PUT /api/v1/campaigns/:campaignSlugName/proposals/bulk
 *
 * Bulk Update Proposal Status for multiple creators
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.requestBody.creators - Array of creators with proposal status details
 */
export const updateCreatorsCampaignsBulkProposalStatus = async (
  req: CreatorsCampaignsBulkProposalStatusRequest,
  init?: RequestInit,
): Promise<BulkProposalUpdateResponse> => {
  const { campaignSlugName, requestBody } = req;

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/proposals/bulk`,
    {
      ...init,
      method: 'PUT',
      body: JSON.stringify(requestBody),
    },
  );

  const response = await res.json<{
    data: BulkProposalUpdateResponse;
  }>();

  return response.data;
};

/**
 * POST /api/v1/campaigns/${campaignSlugName}/proposals/bulk/order-eligible-products
 *
 * Get Order Eligible Products for multiple creators in a campaign
 * @param req.campaignSlugName - The unique identifier for the campaign
 * @param req.creatorIds - Array of creator IDs to fetch eligible products for
 */
export const getCreatorsCampaignsBulkOrderEligibleProducts = async (
  req: CreatorsCampaignsBulkOrderEligibleProductsRequest,
  init?: RequestInit,
): Promise<CreatorsCampaignsBulkOrderEligibleProductsResponse> => {
  const { campaignSlugName, creatorIds } = req;

  const res = await creatorsCampaignsFetch(
    `/${campaignSlugName}/proposals/bulk/order-eligible-products`,
    {
      ...init,
      method: 'POST',
      body: JSON.stringify({ creatorIds }),
    },
  );

  const response = await res.json<{
    data: CreatorsCampaignsBulkOrderEligibleProductsResponse;
  }>();

  return response.data;
};
